import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../assets/web-app-manifest-512x512.png';

const PostIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v10m2 4h-4M4 12h16M4 18h4" />
    </svg>
);

const Navbar = ({ isOpen, setIsOpen }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    
    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const isActiveRoute = (route) => {
        if (route === '/dashboard') {
            return location.pathname === '/dashboard';
        }
        return location.pathname.startsWith(route);
    };

    return (
        <>
            {/* Mobile Overlay */}
            {isOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-30 lg:hidden"
                    onClick={() => setIsOpen(false)}
                />
            )}
            
            {/* Sidebar */}
            <div className={`fixed top-0 left-0 h-full bg-[#111111] border-r border-[#FF3366]/30 transition-all duration-300 z-40 ${
                isOpen ? 'w-64 translate-x-0' : 'w-16 -translate-x-full lg:translate-x-0'
            } lg:${isOpen ? 'w-64' : 'w-16'}`} style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width="20" height="20" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M1 0h2v2H1V0zm4 0h2v2H5V0zm4 0h2v2H9V0zm4 0h2v2h-2V0zm4 0h2v2h-2V0zM1 4h2v2H1V4zm4 0h2v2H5V4zm4 0h2v2H9V4zm4 0h2v2h-2V4zm4 0h2v2h-2V4zM1 8h2v2H1V8zm4 0h2v2H5V8zm4 0h2v2H9V8zm4 0h2v2h-2V8zm4 0h2v2h-2V8zm-12 4h2v2H1v-2zm4 0h2v2H5v-2zm4 0h2v2H9v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2zm-12 4h2v2H1v-2zm4 0h2v2H5v-2zm4 0h2v2H9v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2z" fill="%23111111" fill-opacity="0.2" fill-rule="evenodd"/%3E%3C/svg%3E")'}}>
                <div className={`flex flex-col h-full ${isOpen ? 'w-64' : 'w-16'}`}>
                    {/* Logo/Brand Section */}
                    <div className="h-16 flex items-center justify-between px-4 border-b border-[#FF3366]/30">
                        {isOpen ? (
                            <div className="flex items-center">
                                <div className="relative">
                                    <div className="h-8 w-8 z-10 relative flex items-center justify-center">
                                        <img 
                                            src={Logo}  
                                            alt="UI Admin Logo" 
                                            className="h-8 w-auto"
                                        />
                                    </div>
                                    <div className="absolute inset-0 bg-[#FF3366]/30 filter blur-md rounded-full z-0"></div>
                                </div>
                                <h1 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] ml-2">
                                    UI Admin
                                </h1>
                            </div>
                        ) : (
                            <div 
                                className="flex items-center justify-center w-full cursor-pointer" 
                                onClick={() => setIsOpen(true)}
                            >
                                <div className="relative">
                                    <div className="h-8 w-8 z-10 relative flex items-center justify-center">
                                        <img 
                                            src={Logo}  
                                            alt="UI Admin Logo" 
                                            className="h-8 w-auto"
                                        />
                                    </div>
                                    <div className="absolute inset-0 bg-[#FF3366]/30 filter blur-md rounded-full z-0"></div>
                                </div>
                            </div>
                        )}
                        
                        {isOpen && (
                            <button
                                onClick={() => setIsOpen(!isOpen)}
                                className="text-gray-400 hover:text-[#FF3366] p-2 rounded-lg hover:bg-[#0A0A0A] transition-colors"
                            >
                                {/* More elegant arrow icon with gradient */}
                                <svg 
                                    className="w-5 h-5" 
                                    fill="none" 
                                    viewBox="0 0 24 24" 
                                    stroke="url(#sidebar-toggle-gradient)"
                                >
                                    <defs>
                                        <linearGradient id="sidebar-toggle-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                            <stop offset="0%" stopColor="#FF3366" />
                                            <stop offset="100%" stopColor="#FF6B4D" />
                                        </linearGradient>
                                    </defs>
                                    <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth="2" 
                                        d="M11 19l-7-7 7-7m8 14l-7-7 7-7" 
                                    />
                                </svg>
                            </button>
                        )}
                    </div>

                    {/* Menu Items */}
                    <div className="flex-1 overflow-y-auto py-4 px-3">
                        <nav className="space-y-1">
                            <Link
                                to="/dashboard"
                                className={`h-[56px] flex items-center rounded-lg transition-all duration-200 ${
                                    isActiveRoute('/dashboard')
                                        ? 'bg-gradient-to-r from-[#FF3366]/30 to-[#FF6B4D]/20 border-l-4 border-[#FF3366] text-white font-bold shadow-lg relative overflow-hidden'
                                        : 'text-gray-300 hover:text-white hover:bg-[#0A0A0A] font-medium'
                                }`}
                            >
                                {isActiveRoute('/dashboard') && (
                                    <div className="absolute inset-0 bg-black/20"></div>
                                )}
                                <div className="w-full h-full flex items-center justify-center">
                                    <div className={`flex items-center ${isOpen ? 'w-full px-4' : ''}`}>
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                        </svg>
                                        {isOpen && <span className="ml-3">Dashboard</span>}
                                    </div>
                                </div>
                            </Link>

                            <Link
                                to="/posts"
                                className={`h-[56px] flex items-center rounded-lg transition-all duration-200 ${
                                    isActiveRoute('/posts')
                                        ? 'bg-gradient-to-r from-[#FF3366]/30 to-[#FF6B4D]/20 border-l-4 border-[#FF3366] text-white font-bold shadow-lg relative overflow-hidden'
                                        : 'text-gray-300 hover:text-white hover:bg-[#0A0A0A] font-medium'
                                }`}
                            >
                                {isActiveRoute('/posts') && (
                                    <div className="absolute inset-0 bg-black/20"></div>
                                )}
                                <div className="w-full h-full flex items-center justify-center">
                                    <div className={`flex items-center ${isOpen ? 'w-full px-4' : ''}`}>
                                        <PostIcon />
                                        {isOpen && <span className="ml-3">Posts</span>}
                                    </div>
                                </div>
                            </Link>

                            <Link
                                to="/tasks"
                                className={`h-[56px] flex items-center rounded-lg transition-all duration-200 ${
                                    isActiveRoute('/tasks')
                                        ? 'bg-gradient-to-r from-[#FF3366]/30 to-[#FF6B4D]/20 border-l-4 border-[#FF3366] text-white font-bold shadow-lg relative overflow-hidden'
                                        : 'text-gray-300 hover:text-white hover:bg-[#0A0A0A] font-medium'
                                }`}
                            >
                                {isActiveRoute('/tasks') && (
                                    <div className="absolute inset-0 bg-black/20"></div>
                                )}
                                <div className="w-full h-full flex items-center justify-center">
                                    <div className={`flex items-center ${isOpen ? 'w-full px-4' : ''}`}>
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                        </svg>
                                        {isOpen && <span className="ml-3">Tasks</span>}
                                    </div>
                                </div>
                            </Link>

                            <Link
                                to="/users"
                                className={`h-[56px] flex items-center rounded-lg transition-all duration-200 ${
                                    isActiveRoute('/users')
                                        ? 'bg-gradient-to-r from-[#FF3366]/30 to-[#FF6B4D]/20 border-l-4 border-[#FF3366] text-white font-bold shadow-lg relative overflow-hidden'
                                        : 'text-gray-300 hover:text-white hover:bg-[#0A0A0A] font-medium'
                                }`}
                            >
                                {isActiveRoute('/users') && (
                                    <div className="absolute inset-0 bg-black/20"></div>
                                )}
                                <div className="w-full h-full flex items-center justify-center">
                                    <div className={`flex items-center ${isOpen ? 'w-full px-4' : ''}`}>
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                        </svg>
                                        {isOpen && <span className="ml-3">Users</span>}
                                    </div>
                                </div>
                            </Link>

                            <Link
                                to="/learning-paths"
                                className={`h-[56px] flex items-center rounded-lg transition-all duration-200 ${
                                    isActiveRoute('/learning-paths')
                                        ? 'bg-gradient-to-r from-[#FF3366]/30 to-[#FF6B4D]/20 border-l-4 border-[#FF3366] text-white font-bold shadow-lg relative overflow-hidden'
                                        : 'text-gray-300 hover:text-white hover:bg-[#0A0A0A] font-medium'
                                }`}
                            >
                                {isActiveRoute('/learning-paths') && (
                                    <div className="absolute inset-0 bg-black/20"></div>
                                )}
                                <div className="w-full h-full flex items-center justify-center">
                                    <div className={`flex items-center ${isOpen ? 'w-full px-4' : ''}`}>
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                        </svg>
                                        {isOpen && <span className="ml-3">Learning Paths</span>}
                                    </div>
                                </div>
                            </Link>

                            {/* Sadece superadmin için Admins linki */}
                            {user?.role === 'superadmin' && (
                                                                    <Link
                                        to="/admins"
                                        className={`h-[56px] flex items-center rounded-lg transition-all duration-200 ${
                                            isActiveRoute('/admins')
                                                ? 'bg-gradient-to-r from-[#FF3366]/30 to-[#FF6B4D]/20 border-l-4 border-[#FF3366] text-white font-bold shadow-lg relative overflow-hidden'
                                                : 'text-gray-300 hover:text-white hover:bg-[#0A0A0A] font-medium'
                                        }`}
                                >
                                    {isActiveRoute('/admins') && (
                                        <div className="absolute inset-0 bg-black/20"></div>
                                    )}
                                    <div className="w-full h-full flex items-center justify-center">
                                        <div className={`flex items-center ${isOpen ? 'w-full px-4' : ''}`}>
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                            {isOpen && <span className="ml-3">Admins</span>}
                                        </div>
                                    </div>
                                </Link>
                            )}
                        </nav>
                    </div>

                    {/* Action Buttons - Bottom */}
                    <div className={`${isOpen ? 'p-4' : 'p-2'} border-t border-[#FF3366]/30 space-y-2`}>
                        <Link
                            to="/tasks/new"
                            className={`flex items-center justify-center rounded-lg transition-all duration-200 bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white hover:from-[#FF3366]/90 hover:to-[#FF6B4D]/90 shadow-md ${
                                isOpen ? 'px-4 py-2 gap-2' : 'h-[44px]'
                            }`}
                        >
                            <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                            </svg>
                            {isOpen && <span>Create Task</span>}
                        </Link>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Navbar; 